import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { useI18n } from '@/lib/i18n';
import { fetchHistoriaKarty, type WpisHistoriiKarty } from '@/lib/employees-api';

/**
 * JAKIE NUMERY KARTY MIAŁ TEN PRACOWNIK WCZEŚNIEJ.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZAMÓWIONE 03.09.2026: „dodać na pracowniku przy numerze karty przycisk sprawdzania
 *  historii tego numeru (jak w limitach)".
 *
 *  ⚠️ TO JEST PIERWSZA RZECZ, KTÓREJ TRZEBA PRZY PYTANIU „GDZIE SĄ JEGO STARE WYDANIA". Kaskada
 *  SPS-a przy zmianie numeru NIE RUSZA `TransactionLog` (zmierzone u klienta: 138 wydań bez
 *  właściciela), a nasz portal rusza tylko wtedy, gdy ktoś tego zażądał. Bez tej listy nie da się
 *  ustalić, pod jakim numerem szukać starszych wydań.
 *
 *  ⚠️ WPIS MÓWI TEŻ, CZY ZMIANA DOJECHAŁA DO MASZYN. „Zmienione u nas" i „automat już tak widzi"
 *  to dwa różne zdania — a przy numerze karty ta różnica decyduje o tym, czy człowiek dziś pobierze.
 *
 *  ⚠️ „ZMIANY PORTALU", NIE „HISTORIA KARTY". Zmian zrobionych przy maszynie w SPSUtilities tu nie
 *  ma i nigdy nie będzie: portal ich nie składał, więc nie ma o nich wiersza. Co gorsza, taka
 *  zmiana zostawia po sobie DUCHA (dwa wiersze o tej samej nazwie) — o czym ta lista też nie
 *  powie. Nazwa obiecuje dokładnie tyle, ile pokazujemy.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function HistoriaKarty({
  employeeId,
  groupId,
  pracownik,
  onClose,
}: {
  employeeId: string;
  groupId: string;
  pracownik: string;
  onClose: () => void;
}) {
  const { t } = useI18n();

  const dane = useQuery({
    queryKey: ['employees', 'historia-karty', employeeId, groupId],
    queryFn: () => fetchHistoriaKarty(employeeId, groupId),
  });

  const wpisy = dane.data?.wpisy ?? [];

  return (
    <Dialog open onOpenChange={(otwarte) => !otwarte && onClose()}>
      <DialogContent
        className="w-[min(40rem,calc(100vw-2rem))]"
        title={t('histKarty.tytul')}
        description={`${pracownik}${dane.data === undefined ? '' : ` · ${dane.data.numer}`}`}
      >
        {dane.isPending ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('common.loading')}
          </p>
        ) : dane.isError ? (
          <p className="text-sm text-destructive">{(dane.error as Error).message}</p>
        ) : wpisy.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('histKarty.pusto')}</p>
        ) : (
          <ol className="space-y-2">
            {wpisy.map((w) => (
              <Wpis key={w.id} w={w} />
            ))}
          </ol>
        )}

        {/* ⚠️ GRANICA WIEDZY NA KOŃCU LISTY, nie w opisie okna — czyta się ją wtedy, gdy widać wpisy. */}
        <p className="mt-3 border-t border-border pt-2 text-[11px] text-muted-foreground">
          {t('histKarty.zakresWiedzy')}
        </p>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Wpis({ w }: { w: WpisHistoriiKarty }) {
  const { t } = useI18n();
  const dojechalo = w.potwierdzono !== null;

  return (
    <li className="rounded-md border border-border p-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        {/*
          ⚠️ OBA NUMERY OBOK SIEBIE, ze strzałką — bo pytanie brzmi „pod jakim numerem szukać
          starszych wydań", a odpowiedzią jest ten po LEWEJ. Sam nowy numer nie mówi nic.
        */}
        <span className="font-mono tabular-nums">{w.zNumeru}</span>
        <ArrowRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="font-mono font-medium tabular-nums">{w.naNumer}</span>
        <span className="ml-auto text-xs text-muted-foreground">{czas(w.kiedy)}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {w.kto ?? t('histKarty.bezKonta')}
        {' · '}
        {/*
          ⚠️ STAN OPERACJI, NIE SAM CZAS ZAPISU. Wpis niepotwierdzony znaczy, że automat wciąż
          zna STARY numer — a to jest odpowiedź na „dlaczego jego karta nie działa".
        */}
        <span className={dojechalo ? undefined : 'text-[var(--warning)]'}>
          {dojechalo ? t('histKarty.dojechalo') : t('histKarty.wDrodze').replace('{stan}', w.stan)}
        </span>
      </p>
      {/* Tytuł operacji niesie to, czego nie ma w kolumnach: czy przepisano historię wydań. */}
      <p className="mt-0.5 text-[11px] text-muted-foreground">{w.tytul}</p>
    </li>
  );
}

/** `2026-09-02 09:31` — bez sekund, bo to jest znacznik dla człowieka. */
function czas(iso: string): string {
  const d = new Date(iso);
  const dwa = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dwa(d.getMonth() + 1)}-${dwa(d.getDate())} ${dwa(d.getHours())}:${dwa(d.getMinutes())}`;
}
